// src/pages/learners/ConfirmSignupPage.jsx
import { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import Select from 'react-select'
import {
  Loader2, AlertCircle, CheckCircle2, Eye, EyeOff,
  User, Lock, Mail, IdCard, BadgeInfo, CalendarRange, Globe, Phone, ShieldCheck, FileText
} from 'lucide-react'
import { fetchInviteInfo, confirmSignup, confirmJoin, getPrefixes, getCountries } from '@/api/kuizy'
import { checkUsername } from '@/api/auth'
import TermsCheckbox from '@/widgets/content/TermsWidget'
import PdpaCheckbox from '@/widgets/content/PdpaWidget'

const initForm = {
  prefix_id: '',
  job_title: '',
  first_name: '',
  last_name: '',
  nick_name: '',
  birth_year: '',
  country: 'TH',
  phone_code: '+66',
  phone: '',
  username: '',
  password: '',
  email: '',
  confirmPassword: '',
  acceptTerms: false,
  acceptPdpa: false,
}

export default function ConfirmSignupPage() {
  const { token } = useParams()
  const nav = useNavigate()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(false)
  const [already, setAlready] = useState(false)
  const [existingUser, setExistingUser] = useState(false)
  const [apiErr, setApiErr] = useState('')
  const [pwErr, setPwErr] = useState('')
  const [cfErr, setCfErr] = useState('')
  const [byErr, setByErr] = useState('')
  const [userErr, setUserErr] = useState('')
  const [info, setInfo] = useState(null)
  const [prefixes, setPrefixes] = useState([])
  const [countries, setCountries] = useState([])
  const [form, setForm] = useState(initForm)
  const [show1, setShow1] = useState(false)
  const [show2, setShow2] = useState(false)
  const [step, setStep] = useState(1)

  const currYear = new Date().getFullYear()
  const usernameRx = /^[A-Za-z0-9]+$/
  const pwRx = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/

  const passScore = useMemo(() => {
    const p = form.password || ''
    let s = 0
    if (p.length >= 8) s++
    if (/[0-9]/.test(p)) s++
    if (/[A-Z]/.test(p)) s++
    if (/[^A-Za-z0-9]/.test(p)) s++
    return s
  }, [form.password])
  const passLabel = passScore >= 4 ? 'แข็งแรงมาก' : passScore === 3 ? 'แข็งแรง' : passScore === 2 ? 'ปานกลาง' : form.password ? 'อ่อน' : ''

  useEffect(() => {
    (async () => {
      try {
        const [pf, ct, inv] = await Promise.all([
          getPrefixes(), getCountries(), fetchInviteInfo(token)
        ])
        setPrefixes(pf.data || [])
        setCountries(ct.data || [])
        setInfo(inv.data || {})
        console.log('Invite info:', inv.data)
        if (inv?.data?.status === 1 || inv?.data?.status === null) {
          setAlready(true)
          return
        }
        if (inv?.data?.has_account) {
          setExistingUser(true)
        }
        setForm(f => ({
          ...f,
          prefix_id: inv.data?.prefix || '',
          job_title: inv.data?.job_title || '',
          first_name: inv.data?.first_name || '',
          last_name: inv.data?.last_name || '',
          nick_name: inv.data?.nick_name || '',
          birth_year: inv.data?.birth_year || '',
          country: inv.data?.country || 'TH',
          phone_code: inv.data?.phone_code || '+66',
          phone: inv.data?.phone || '',
          email: inv.data?.email || '',
        }))
        console.log('Invite info:', form)
      } catch {
        setApiErr('ลิงก์ไม่ถูกต้องหรือหมดอายุแล้ว')
      } finally {
        setLoading(false)
      }
    })()
  }, [token])

  const selectStyle = { control: b => ({ ...b, minHeight: 42, borderRadius: 12 }) }
  const fieldCls = 'w-full border border-gray-300 rounded-xl py-3 pl-11 pr-10 text-base focus:ring-2 focus:ring-primary focus:outline-none placeholder-gray-400 bg-white/70 disabled:bg-gray-100'
  const badge = (ok) => `flex items-center gap-2 rounded-lg px-2 py-1 text-xs ${ok ? 'text-emerald-700 bg-emerald-50' : 'text-gray-500 bg-gray-50'}`

  const nextStep = async e => {
    e.preventDefault()
    setApiErr('')
    setPwErr('')
    setCfErr('')
    setUserErr('')

    const uname = form.username.trim()
    if (!uname || !usernameRx.test(uname)) {
      setUserErr('Username ต้องเป็นตัวอักษรอังกฤษและตัวเลขเท่านั้น'); return
    }
    try {
      const r = await checkUsername(uname)
      const exists = !!(r?.data?.exists ?? r?.data?.taken ?? (r?.data?.ok === false))
      if (exists) {
        setUserErr('Username นี้ถูกใช้แล้ว'); return
      }
    } catch {
      setApiErr('ไม่สามารถตรวจสอบ Username ได้'); return
    }
    if (!pwRx.test(form.password)) { setPwErr('รหัสผ่านต้องมีอย่างน้อย 8 ตัว และมีทั้งตัวอักษรและตัวเลข'); return }
    if (form.password !== form.confirmPassword) { setCfErr('รหัสผ่านไม่ตรงกัน'); return }
    if (!(form.acceptTerms && form.acceptPdpa)) { setApiErr('กรุณายอมรับข้อกำหนดและเงื่อนไขและนโยบายความเป็นส่วนบุคคล'); return }

    setStep(2)
  }

  const submit = async e => {
    e.preventDefault()
    setSaving(true)
    setApiErr('')
    setByErr('')

    const y = parseInt(String(form.birth_year).trim(), 10)
    if (!form.birth_year || Number.isNaN(y) || y < 1900 || y > currYear) {
      setByErr(`กรุณากรอกปีเกิดเป็นตัวเลขช่วง 1900–${currYear}`)
      setSaving(false)
      return
    }

    try {
      await confirmSignup(token, {
        ...form,
        username: form.username.trim(),
        birth_year: y,
        accept_terms: form.acceptTerms,
        accept_pdpa: form.acceptPdpa,
        accepted_at: new Date().toISOString(),
        terms_version: '2025-09-17',
        privacy_version: '2025-09-17'
      })
      setDone(true)
      setTimeout(() => nav('/learn/login', { replace: true }), 1500)
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.error || 'ไม่สามารถบันทึกได้ กรุณาลองใหม่ภายหลัง'
      setApiErr(msg)
      setSaving(false)
    }
  }

  const confirm = async () => {
    setSaving(true)
    setApiErr('')
    try {
      await confirmJoin(token, {
        accept_terms: true,
        accept_pdpa: true,
        accepted_at: new Date().toISOString(),
        terms_version: '2025-09-17',
        privacy_version: '2025-09-17'
      })
      setDone(true)
      setTimeout(() => nav('/learn/login', { replace: true }), 1500)
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.error || 'ไม่สามารถบันทึกได้ กรุณาลองใหม่ภายหลัง'
      setApiErr(msg)
      setSaving(false)
    }
  }

  if (loading)
    return (
      <div className='min-h-screen flex items-center justify-center bg-gradient-to-tr from-primary via-info to-secondary'>
        <Loader2 className='w-10 h-10 animate-spin text-white' />
      </div>
    )

  if (apiErr && !info)
    return (
      <div className='min-h-screen flex items-center justify-center p-4 bg-gradient-to-tr from-primary via-info to-secondary'>
        <div className='w-full max-w-md bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl p-8 space-y-4 text-center'>
          <AlertCircle className='w-10 h-10 text-danger mx-auto' />
          <p className='text-danger text-sm'>{apiErr}</p>
          <button
            className='bg-primary hover:bg-secondary text-white rounded-lg px-5 py-2 text-sm font-semibold'
            onClick={() => window.location.reload()}
          >
            ลองใหม่อีกครั้ง
          </button>
        </div>
      </div>
    )

  if (already)
    return (
      <div className='min-h-screen flex items-center justify-center p-4 bg-gradient-to-tr from-primary via-info to-secondary'>
        <div className='w-full max-w-md bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl p-8 space-y-4 text-center'>
          <CheckCircle2 className='w-10 h-10 text-success mx-auto' />
          <p className='text-success text-sm'>ท่านได้ยืนยันไปแล้ว</p>
          <button
            className='bg-primary hover:bg-secondary text-white rounded-lg px-5 py-2 text-sm font-semibold'
            onClick={() => nav('/learn/login', { replace: true })}
          >
            ไปยังหน้าล็อกอิน
          </button>
        </div>
      </div>
    )

  if (done)
    return (
      <div className='min-h-screen flex items-center justify-center p-4 bg-gradient-to-tr from-primary via-info to-secondary'>
        <div className='w-full max-w-md bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl p-8 space-y-4 text-center'>
          <CheckCircle2 className='w-10 h-10 text-success mx-auto' />
          <p className='text-success text-sm'>ลงทะเบียนสำเร็จ!</p>
        </div>
      </div>
    )

  const prefixName = prefixes.find(p => String(p.id) === String(form.prefix_id))?.name_th || ''

  return (
    <section className='min-h-screen flex items-center justify-center bg-gradient-to-tr from-primary via-info to-secondary p-4'>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: 'easeOut' }}
        className='w-full max-w-2xl bg-white/85 backdrop-blur-xl rounded-2xl shadow-2xl p-8'
      >
        <div className='text-center space-y-2 mb-6'>
          <img src='/images/app-logo.png' alt='Kuizy' className='h-16 mx-auto' />
          <h1 className='text-2xl font-extrabold text-gray-900'>{existingUser ? 'ยืนยันคำเชิญ' : 'ยืนยันข้อมูล & ตั้งรหัสผ่าน'}</h1>
          <p className='text-sm text-gray-500'>{existingUser ? 'ยืนยันการเข้าร่วมองค์กร' : 'กรุณากรอกข้อมูลให้ครบถ้วน'}</p>
        </div>

        <div className='border border-gray-200 rounded-xl p-4 mb-6 grid gap-2 md:grid-cols-3 text-sm bg-white'>
          <div className='flex items-center gap-2'><BadgeInfo className='h-4 w-4 text-primary' /><span className='text-gray-500'>กลุ่มผู้เรียน</span></div>
          <div className="md:col-span-2 flex gap-2 flex-wrap">
            {info?.groups?.length > 0 ? (
              info.groups.map(g => (
                <span
                  key={g.group_id}
                  className="px-2 py-1 text-xs bg-blue-100 rounded"
                >
                  {g.group_name}
                </span>
              ))
            ) : (
              <span>Member</span>
            )}
          </div>


          <div className='flex items-center gap-2 md:col-span-1'><Mail className='h-4 w-4 text-primary' /><span className='text-gray-500'>อีเมล</span></div>
          <div className='md:col-span-2 break-all'>{info?.email}</div>
          <div className='flex items-center gap-2 md:col-span-1'><User className='h-4 w-4 text-primary' /><span className='text-gray-500'>ชื่อ–สกุล</span></div>
          <div className='md:col-span-2'>{`${prefixName || ''} ${form?.first_name || ''} ${form?.last_name || ''}`.trim()}</div>
        </div>

        {existingUser ? (
          <div className='text-center'>
            <button
              onClick={confirm}
              className='bg-gradient-to-r from-primary to-secondary text-white rounded-xl px-5 py-3 text-sm font-semibold w-full flex items-center justify-center gap-2 disabled:opacity-50'
            >
              {saving && <Loader2 className='w-4 h-4 animate-spin' />}
              {saving ? 'กำลังบันทึก…' : 'ยืนยันการเข้าร่วม'}
            </button>
          </div>
        ) : (
          step === 1 ? (
            <form className='flex flex-col gap-4' onSubmit={nextStep}>
              <div className='relative'>
                <User className='absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400' />
                <input
                  placeholder='กรุณาตั้ง Username'
                  value={form.username}
                  onChange={e => setForm({ ...form, username: e.target.value.replace(/[^A-Za-z0-9]/g, '') })}
                  className={fieldCls}
                  required
                  pattern='[A-Za-z0-9]+'
                  autoComplete='username'
                />
              </div>

              <div className='space-y-2'>
                <div className='relative'>
                  <Lock className='absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400' />
                  <input
                    type={show1 ? 'text' : 'password'}
                    placeholder='ตั้งรหัสผ่าน (อย่างน้อย 8 ตัว ผสมตัวอักษรและตัวเลข)'
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    className={fieldCls}
                    required
                    autoComplete='new-password'
                  />
                  <button type='button' tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => setShow1(v => !v)} className='absolute right-4 top-1/2 -translate-y-1/2 text-gray-500'>
                    {show1 ? <EyeOff className='h-5 w-5' /> : <Eye className='h-5 w-5' />}
                  </button>
                </div>

                <div className='grid grid-cols-2 gap-2 text-xs'>
                  <div className={badge(form.password.length >= 8)}><CheckCircle2 className='h-4 w-4' /> 8 ตัวอักษรขึ้นไป</div>
                  <div className={badge(/[0-9]/.test(form.password))}><CheckCircle2 className='h-4 w-4' /> มีตัวเลขอย่างน้อย 1 ตัว</div>
                  <div className={badge(/[A-Za-z]/.test(form.password))}><CheckCircle2 className='h-4 w-4' /> มีตัวอักษร</div>
                  <div className={badge(/[^A-Za-z0-9]/.test(form.password))}><CheckCircle2 className='h-4 w-4' /> อักขระพิเศษ (แนะนำ)</div>
                </div>
                {passLabel && <div className='text-xs text-gray-600'>ความแข็งแรงรหัสผ่าน: {passLabel}</div>}
              </div>

              <div className='relative'>
                <Lock className='absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400' />
                <input
                  type={show2 ? 'text' : 'password'}
                  placeholder='ยืนยันรหัสผ่าน'
                  value={form.confirmPassword}
                  onChange={e => setForm({ ...form, confirmPassword: e.target.value })}
                  className={fieldCls}
                  required
                  autoComplete='new-password'
                />
                <button type='button' tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => setShow2(v => !v)} className='absolute right-4 top-1/2 -translate-y-1/2 text-gray-500'>
                  {show2 ? <EyeOff className='h-5 w-5' /> : <Eye className='h-5 w-5' />}
                </button>
              </div>

              <div className='space-y-3'>
                <div className='flex items-center gap-2 text-sm text-gray-600'>
                  <FileText className='h-4 w-4 text-primary' />
                  <span>โปรดอ่านและยอมรับเพื่อเปิดใช้บัญชี</span>
                </div>
                <TermsCheckbox checked={form.acceptTerms} onChange={v => setForm({ ...form, acceptTerms: v })} />
                <PdpaCheckbox checked={form.acceptPdpa} onChange={v => setForm({ ...form, acceptPdpa: v })} />
                <div className='flex items-center gap-2 text-xs text-gray-500'>
                  <ShieldCheck className='h-4 w-4' />
                  สามารถถอนความยินยอมด้านการตลาดหรือโปรไฟล์ได้ภายหลังในหน้าการตั้งค่า
                </div>
              </div>

              {userErr && <p className='text-danger text-xs'>{userErr}</p>}
              {pwErr && <p className='text-danger text-xs'>{pwErr}</p>}
              {cfErr && <p className='text-danger text-xs'>{cfErr}</p>}
              {apiErr && <p className='text-danger text-xs'>{apiErr}</p>}

              <button
                type='submit'
                className='bg-gradient-to-r from-primary to-secondary text-white rounded-xl px-5 py-3 text-sm font-semibold w-full flex items-center justify-center gap-2'
              >
                ถัดไป
              </button>
            </form>
          ) : (
            <form className='space-y-6' onSubmit={submit}>
              <div className='grid gap-4 md:grid-cols-2 text-sm'>

                <div className='flex flex-col gap-1'>
                  <label className='flex items-center gap-2 text-gray-600'><IdCard className='h-4 w-4 text-primary' />คำนำหน้า</label>
                  <Select
                    styles={selectStyle}
                    classNamePrefix='rs'
                    options={prefixes.map(p => ({ value: p.id, label: p.name_th }))}
                    value={form.prefix_id ? {
                      value: form.prefix_id,
                      label: prefixes.find(p => String(p.id) === String(form.prefix_id))?.name_th
                    } : null}
                    onChange={o => setForm({ ...form, prefix_id: o?.value || '' })}
                    isClearable
                  />
                </div>

                <div className='flex flex-col gap-1'>
                  <label className='flex items-center gap-2 text-gray-600'><BadgeInfo className='h-4 w-4 text-primary' />ตำแหน่งงาน</label>
                  <div className='relative'>

                    <input value={form.job_title} onChange={e => setForm({ ...form, job_title: e.target.value })} className='w-full border border-gray-300 rounded-xl py-2.5 px-3 focus:ring-2 focus:ring-primary' />
                  </div>
                </div>

                <div className='flex flex-col gap-1'>
                  <label className='flex items-center gap-2 text-gray-600'><User className='h-4 w-4 text-primary' />ชื่อ</label>
                  <div className='relative'>

                    <input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} className='w-full border border-gray-300 rounded-xl py-2.5 px-3 focus:ring-2 focus:ring-primary' required />
                  </div>
                </div>

                <div className='flex flex-col gap-1'>
                  <label className='flex items-center gap-2 text-gray-600'><User className='h-4 w-4 text-primary' />นามสกุล</label>
                  <div className='relative'>

                    <input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} className='w-full border border-gray-300 rounded-xl py-2.5 px-3 focus:ring-2 focus:ring-primary' required />
                  </div>
                </div>

                <div className='flex flex-col gap-1'>
                  <label className='text-gray-600'>ชื่อเล่น</label>
                  <input value={form.nick_name} onChange={e => setForm({ ...form, nick_name: e.target.value })} className='w-full border border-gray-300 rounded-xl py-2.5 px-3 focus:ring-2 focus:ring-primary' />
                </div>

                <div className='flex flex-col gap-1'>
                  <label className='flex items-center gap-2 text-gray-600'><CalendarRange className='h-4 w-4 text-primary' />ปีเกิด</label>
                  <div className='relative'>

                    <input type='number' value={form.birth_year} onChange={e => { setForm({ ...form, birth_year: e.target.value }); setByErr('') }} className='w-full border border-gray-300 rounded-xl py-2.5 px-3 focus:ring-2 focus:ring-primary' min={1900} max={currYear} required />
                  </div>
                  {byErr && <p className='text-danger text-xs'>{byErr}</p>}
                </div>

                <div className='flex flex-col gap-1 md:col-span-2'>
                  <label className='flex items-center gap-2 text-gray-600'><Globe className='h-4 w-4 text-primary' />ประเทศ</label>
                  <Select
                    styles={selectStyle}
                    classNamePrefix='rs'
                    options={countries.map(c => ({ value: c.iso2, label: `${c.name_en} (${c.phone_code})` }))}
                    value={form.country ? { value: form.country, label: countries.find(c => c.iso2 === form.country)?.name_en } : null}
                    onChange={o => {
                      const c = countries.find(x => x.iso2 === (o?.value || ''))
                      setForm({ ...form, country: o?.value || 'TH', phone_code: c?.phone_code || form.phone_code })
                    }}
                    isClearable
                  />
                </div>

                <div className='flex flex-col gap-1 md:col-span-2'>
                  <label className='flex items-center gap-2 text-gray-600'><Phone className='h-4 w-4 text-primary' />โทรศัพท์</label>
                  <div className='flex gap-2'>
                    <input className='w-28 border border-gray-300 rounded-xl py-2.5 px-3 focus:ring-2 focus:ring-primary' value={form.phone_code} onChange={e => setForm({ ...form, phone_code: e.target.value })} />
                    <input className='flex-1 border border-gray-300 rounded-xl py-2.5 px-3 focus:ring-2 focus:ring-primary' value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                  </div>
                </div>
              </div>

              <button type='submit' disabled={saving} className='bg-gradient-to-r from-primary to-secondary text-white rounded-xl px-5 py-3 text-sm font-semibold w-full flex items-center justify-center gap-2 disabled:opacity-50'>
                {saving && <Loader2 className='w-4 h-4 animate-spin' />}
                {saving ? 'กำลังบันทึก…' : 'ยืนยัน & สร้างบัญชี'}
              </button>

              {apiErr && <p className='text-danger text-center text-xs mt-2'>{apiErr}</p>}
            </form>
          )

        )
        }
      </motion.div>
    </section>
  )
}
